/**
 * Order Tracking Page Component
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import api from '../services/api';
import Loading from '../components/common/Loading';
import { toast } from 'react-hot-toast';

const OrderTrackingPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit } = useForm({
    defaultValues: {
      order_number: searchParams.get('order_number') || ''
    }
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const res = await api.get('/orders/track/', {
        params: { order_number: data.order_number }
      });
      setOrder(res.data);
    } catch (error) {
      console.error('Error tracking order:', error);
      toast.error('الطلب غير موجود');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-blue-100 text-blue-800',
      ready: 'bg-green-100 text-green-800',
      shipping: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-500 text-white',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const statuses = {
      pending: 'قيد الانتظار',
      preparing: 'قيد التحضير',
      ready: 'جاهز للشحن',
      shipping: 'قيد الشحن',
      delivered: 'تم التسليم',
      cancelled: 'ملغي',
    };
    return statuses[status] || status;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8 text-center">{t('order.track_order')}</h1>
      
      {/* Search Form */}
      <div className="max-w-2xl mx-auto mb-12">
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-6 rounded-lg shadow-md">
          <label className="block mb-2 font-semibold">
            {t('order.order_number')}
          </label>
          <div className="flex gap-4">
            <input
              {...register('order_number')}
              className="flex-grow px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="ORD-XXXXXXXX"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'جاري البحث...' : 'تتبع'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Order Details */}
      {order && (
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-8 rounded-lg shadow-md">
            {/* Header */}
            <div className="border-b pb-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    طلب رقم: {order.order_number}
                  </h2>
                  <p className="text-gray-600">
                    تاريخ الطلب: {new Date(order.created_at).toLocaleDateString('ar-EG')}
                  </p>
                </div>
                <span className={`px-6 py-2 rounded-full font-bold ${getStatusColor(order.status)}`}>
                  {getStatusText(order.status)}
                </span>
              </div>
            </div>
            
            {/* Customer Info */}
            <div className="mb-6">
              <h3 className="text-xl font-bold mb-4">معلومات العميل</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                <div>
                  <span className="font-semibold">الاسم:</span> {order.customer_name}
                </div>
                <div>
                  <span className="font-semibold">الهاتف:</span> {order.customer_phone}
                </div>
                {order.customer_email && (
                  <div className="md:col-span-2">
                    <span className="font-semibold">البريد:</span> {order.customer_email}
                  </div>
                )}
              </div>
            </div>
            
            {/* Shipping Info */}
            <div className="mb-6 border-t pt-6">
              <h3 className="text-xl font-bold mb-4">معلومات التوصيل</h3>
              <div className="text-gray-700">
                <p className="mb-2">
                  <span className="font-semibold">المحافظة:</span> {order.province}
                </p>
                <p className="mb-2">
                  <span className="font-semibold">القضاء:</span> {order.district}
                </p>
                <p className="mb-2">
                  <span className="font-semibold">العنوان:</span> {order.address}
                </p>
              </div>
            </div>
            
            {/* Items */}
            <div className="mb-6 border-t pt-6">
              <h3 className="text-xl font-bold mb-4">المنتجات</h3>
              <div className="space-y-3">
                {order.items.map((item) => (
                  <div key={item.id} className="flex justify-between items-center py-3 border-b">
                    <div>
                      <p className="font-semibold">{item.product_name}</p>
                      <p className="text-gray-600 text-sm">الكمية: {item.quantity}</p>
                    </div>
                    <p className="font-bold">{item.subtotal.toLocaleString()} د.ع</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Total */}
            <div className="border-t pt-6">
              <div className="flex justify-between items-center text-2xl font-bold">
                <span>الإجمالي</span>
                <span className="text-blue-600">{order.total.toLocaleString()} د.ع</span>
              </div>
            </div>
            
            {/* Invoice Download */}
            {order.invoice_pdf && (
              <div className="mt-6">
                <a
                  href={order.invoice_pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700"
                >
                  تحميل الفاتورة PDF
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTrackingPage;

